import type { ScaleAcronym } from './scoreColor';

export type LevelState = 'active' | 'filled' | 'muted';

export interface ScaleLevel {
  score: number;
  label: string;
}

export type LevelVariant = 'standard' | 'silver' | 'debunked';

/** -- ETS levels high → low (ATLASSYSTEM8 / design.md) */
export const ETS_LEVELS: readonly ScaleLevel[] = [
  { score: 4, label: 'Complete Mechanistic Theory' },
  { score: 3, label: 'Proven Phenomenon Theory' },
  { score: 2, label: 'Strongly Supported Theory' },
  { score: 1, label: 'Emerging Supported Theory' },
  { score: 0, label: 'Indeterminate' },
  { score: -1, label: 'Debunked' },
] as const;

/** -- SES levels high → low */
export const SES_LEVELS: readonly ScaleLevel[] = [
  { score: 3, label: 'Core Scientific Program' },
  { score: 2, label: 'Strong Scientific Engagement' },
  { score: 1, label: 'Partial Scientific Engagement' },
  { score: 0, label: 'No Scientific Interaction' },
  { score: -1, label: 'Methodological Insulation' },
  { score: -2, label: 'Anti-Scientific' },
] as const;

/** -- EIS levels high → low */
export const EIS_LEVELS: readonly ScaleLevel[] = [
  { score: 3, label: 'Architectonic Integrator' },
  { score: 2, label: 'Cross-Domain Integrator' },
  { score: 1, label: 'Domain Integrator' },
  { score: 0, label: 'Standalone Theory' },
  { score: -1, label: 'Fragmenting Theory' },
  { score: -2, label: 'Systemically Incompatible Doctrine' },
] as const;

export const SCALE_LEVELS: Record<ScaleAcronym, readonly ScaleLevel[]> = {
  ETS: ETS_LEVELS,
  SES: SES_LEVELS,
  EIS: EIS_LEVELS,
};

/**
 * Shared ladder rows (high → low) so SES / ETS / EIS zeros align.
 * ETS owns +4; SES/EIS own −2; missing cells are spacers.
 */
export const LADDER_ALIGN_SCORES = [4, 3, 2, 1, 0, -1, -2] as const;

/**
 * Column-graph fill state for a level given the scale's current score.
 * Positive: active at S ("party floor"), filled 0..S-1 ("basic lights"), muted elsewhere.
 * Negative: active at S (red), filled from S+1 up through 0 (includes the zero floor), muted above 0.
 * Zero: only 0 active; everything else muted.
 */
export function getLevelState(score: number, level: number): LevelState {
  if (score === level) return 'active';

  if (score > 0) {
    if (level >= 0 && level < score) return 'filled';
    return 'muted';
  }

  if (score < 0) {
    // -- Light the path from the score up through zero (0 is the ground floor)
    if (level > score && level <= 0) return 'filled';
    return 'muted';
  }

  // -- score === 0
  return 'muted';
}

/** -- ETS special chrome; SES/EIS are always standard */
export function getLevelVariant(
  acronym: ScaleAcronym,
  level: number
): LevelVariant {
  if (acronym !== 'ETS') return 'standard';
  if (level === -1) return 'debunked';
  if (level >= 3) return 'silver';
  return 'standard';
}

export function getCanonicalLabel(
  acronym: ScaleAcronym,
  score: number
): string {
  const levels = SCALE_LEVELS[acronym];
  return levels.find((l) => l.score === score)?.label ?? '';
}
